import { useMemo } from 'react';
import { getApp, listApps } from '@/apps/registry';
import { pinnedAppIds } from '@/config/startMenu';
import type { AppGroup, AppManifest } from '@/apps/types';

/** The order XP filed its Programs folders in. */
const GROUP_ORDER: readonly AppGroup[] = ['Accessories', 'Games', 'Internet', 'System'];

export interface ProgramGroup {
  name: AppGroup;
  apps: AppManifest[];
}

export interface StartMenuItems {
  /** Top-left block: explicitly pinned programs, in config order. */
  pinned: AppManifest[];
  /** Below the rule: everything else that is installed. */
  recent: AppManifest[];
  /** All Programs entries with no `group` — listed above the folders. */
  loose: AppManifest[];
  /** All Programs folders, empty ones dropped. */
  groups: ProgramGroup[];
}

/**
 * Pinned programs: `pinnedAppIds` sets the order, then any manifest that
 * declares `pinned: true` and wasn't already named. Unknown ids are skipped
 * rather than rendered as holes, so a typo in config degrades quietly.
 */
export function getPinnedApps(): AppManifest[] {
  const out: AppManifest[] = [];
  const taken = new Set<string>();

  for (const id of pinnedAppIds) {
    if (taken.has(id)) continue;
    const app = getApp(id);
    if (!app) continue;
    taken.add(id);
    out.push(app);
  }

  for (const app of listApps()) {
    if (app.pinned && !taken.has(app.id)) {
      taken.add(app.id);
      out.push(app);
    }
  }

  return out;
}

/** XP kept six rows below the rule and pushed the rest into All Programs. */
const RECENT_LIMIT = 6;

/**
 * There is no usage history to mine, so the "recently used" strip below the
 * pinned block simply shows the rest of what is installed, in registry order,
 * up to the same six rows the real menu made room for. Everything is reachable
 * from All Programs regardless, so the cut costs nothing.
 */
export function getRecentApps(pinned: readonly AppManifest[]): AppManifest[] {
  const pinnedIds = new Set(pinned.map((a) => a.id));
  return listApps()
    .filter((a) => !pinnedIds.has(a.id))
    .slice(0, RECENT_LIMIT);
}

/** The All Programs tree: ungrouped programs first, then the folders. */
export function getProgramTree(): { loose: AppManifest[]; groups: ProgramGroup[] } {
  const all = listApps();
  const loose = all.filter((a) => !a.group);

  const groups: ProgramGroup[] = [];
  for (const name of GROUP_ORDER) {
    const apps = all.filter((a) => a.group === name);
    if (apps.length > 0) groups.push({ name, apps });
  }

  return { loose, groups };
}

/**
 * Everything the Start menu renders, derived once. The registry is a module
 * constant, so an empty dependency list is correct and keeps the components
 * free of derivation logic.
 */
export function useStartMenuItems(): StartMenuItems {
  return useMemo<StartMenuItems>(() => {
    const pinned = getPinnedApps();
    const { loose, groups } = getProgramTree();
    return { pinned, recent: getRecentApps(pinned), loose, groups };
  }, []);
}
